import { useState, useEffect, ReactPortal } from 'react';
import { createPortal } from 'react-dom';
import { Circle, Xmark } from './../../assets/Icons';
import type { TFeaturedIconOutlineColor } from '../../types/featured_icon/featured_icon_outline/featuredIconOutline';
import type {
  INotification,
  TNotificationState,
} from '../../types/notification/notification';
import { FeaturedIconOutline } from '../FeaturedIcon/FeaturedIconOutline';
import customEvents from '../../utils/customEvents';
import { Button } from '../Button';
import styles from './notification.module.scss';


const iconColorMap: Record<TNotificationState, TFeaturedIconOutlineColor> = {
  normal: 'gray',
  success: 'success',
  warning: 'warning',
  danger: 'error',
  primary: 'brand',
};

export const Notification = ({
  state = 'normal',
  title,
  hintText,
  btnsProps,
  onCloseHandler,
  onShowHandler,
  type = 'alert',
  icon = Circle,
  isMobile = false,
  width = '100%',
  maxWidth = '100%',
  show,
  hasCloseIcon,
  position = 'relative',
  placement = 'top_end',
  disableAnimation = false,
  timeout = 0, // seconds
  isCreatePortal = false,
}: INotification): JSX.Element | ReactPortal | null  => {

  const [isShown, setIsShown] = useState(show);
  const [modalStack, setModalStack] = useState<HTMLElement[]>([]);
  const timeoutInMs = timeout * 1000;

  useEffect(() => {
    const handleOpen = (event: Event) => {
      // event is a CustomEvent with detail containing currentRef and isOverlayAboveModal come from modal event dispatch when open
      const custom = event as CustomEvent<{ currentRef: HTMLElement | null; isOverlayAboveModal: boolean }>;

      // if the modal is opened isOverlayAboveModal is true, we add it to the stack so that the notification can be rendered above it
      if (custom.detail?.isOverlayAboveModal && custom.detail?.currentRef) {
        setModalStack((prev) => [...prev, custom.detail.currentRef as HTMLElement]);
      }
    };

    const handleClose = (event: Event) => {
      const custom = event as CustomEvent<{ currentRef: HTMLElement | null; isOverlayAboveModal: boolean }>;

      // if the modal is closed isOverlayAboveModal is true, we remove it from the stack so that the notification can be rendered above the next modal in the stack
      if (custom.detail?.isOverlayAboveModal && custom.detail?.currentRef) {
        setModalStack((prev) => prev.filter((ref) => ref !== custom.detail.currentRef));
      }
    };

    customEvents.addEventListener("modal-open", handleOpen);
    customEvents.addEventListener("modal-close", handleClose);

    return () => {
      customEvents.removeEventListener("modal-open", handleOpen);
      customEvents.removeEventListener("modal-close", handleClose);
    };
  }, []);

  // If there are modals in the stack, we render the notification above the last modal in the stack
  const container = modalStack[modalStack.length - 1] ?? null;  

  const handleClose = () => {
    setIsShown(false);
    onCloseHandler?.();
  };

  useEffect(() => {
    setIsShown(show);
  }, [show]);

  useEffect(() => {
    if (timeout && isShown) {
      onShowHandler?.();
      const timer = setTimeout(() => {
        handleClose();
      }, timeoutInMs);

      return () => clearTimeout(timer);
    }
  }, [timeout, isShown, onCloseHandler]);

  const notificationElement = (
    <div
      data-notification-show={isShown}
      data-notification-animation-enabled={!disableAnimation}
      data-notification-type={type}
      data-notification-is-mobile={isMobile}
      data-notification-position={position}
      data-notification-width={width}
      data-notification-max-width={width}
      {...(position !== 'relative' ? { 'data-notification-placement': placement } : {})}
      className={styles['notification']}
      style={{ width, maxWidth, position }}
    >
      <div className={styles['notification__indicator-icon']}>
        <FeaturedIconOutline
          icon={icon}
          size="md"
          color={iconColorMap[state]}
        />
      </div>
      <div className={styles['notification__info-box']}>

        <h3 className={styles['notification__info-box__title']}>{title}</h3>

        {
          hintText && (
            <p className={styles['notification__info-box__hintText']}>
              {hintText}
            </p>
          )
        }

        {
          btnsProps && (
            <div className={styles['notification__info-box__action-btns-box']}>
              {btnsProps.map((btn, index) =>
                <Button
                  key={`notification-btn-${index}`}
                  size="md"
                  {...btn}
                />
              )}
            </div>
          )
        }
      </div>

      {
        hasCloseIcon && onCloseHandler &&
        <button
          className={styles['notification__close-btn']}
          onClick={handleClose}
        >
          {Xmark}
        </button>
      }
    </div>
  );

  if (isCreatePortal) {
    return createPortal(notificationElement, document.body);
  }

  if (container) {
    return createPortal(notificationElement, container);
  }

  return notificationElement;
};